"use client";
import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import {
  getItemId,
  getItemUrl,
  getItemUrlById,
  getItemUrlByPathname,
} from "@/utils/itemUrlUtils";
import { Item } from "@/dto/item";

interface UseItemRedirectProps {
  /**
   * If true, the redirect logic is skipped.
   */
  shouldSkip?: boolean;
  /**
   * The path where no item redirect should happen (e.g. "/services/all").
   * If current pathname equals this, no redirect check is performed.
   */
  ignorePath?: string;
  /**
   * Function to construct the full target URL given the canonical itemUrl (slug-id).
   */
  // eslint-disable-next-line no-unused-vars
  constructUrlAction: (_url: string) => string;
  /**
   * Optional list of items to search for the item ID before making a server call.
   */
  items?: Item[] | null;
}

export const useItemRedirect = ({
  shouldSkip = false,
  ignorePath,
  constructUrlAction,
  items,
}: UseItemRedirectProps) => {
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    if (shouldSkip) return;
    if (ignorePath && pathname === ignorePath) return;
    if (!pathname) return;

    const itemId = getItemId(pathname);
    if (!itemId) return;

    const checkRedirect = async () => {
      let itemUrl: string | null | undefined = null;

      if (items && items.length > 0) {
        const item = items.find((i: any) => i.id === itemId);
        if (item) {
          itemUrl = getItemUrl(item);
        }
      }

      itemUrl ??= await getItemUrlById(itemId);

      if (!itemUrl) return;

      const targetUrl = constructUrlAction(itemUrl);

      // Case 1: ID-only path (no slug)
      if (!pathname.includes("-")) {
        if (itemId !== itemUrl && pathname !== targetUrl) {
          router.replace(targetUrl, { scroll: false });
        }
        return;
      }

      // Case 2: Path with slug
      const pathItemUrl = getItemUrlByPathname(pathname);

      if (pathItemUrl !== itemUrl && pathname !== targetUrl) {
        router.replace(targetUrl, { scroll: false });
      }
    };

    checkRedirect();
  }, [pathname, shouldSkip, ignorePath, constructUrlAction, router, items]);
};
